import React, { useState } from 'react';
import {
  Lock, Zap, CheckCircle, ArrowRight, Mail,
  Globe, Search, Package, Link2, Radio, Shield, Layers, GitCompare, Tag, Activity
} from 'lucide-react';

const TOOL_DETAILS = {
  '/sellers-json': {
    icon: Search,
    name: 'Sellers.json Checker',
    color: 'green',
    pitch: 'Instantly validate any SSP\'s sellers.json, cross-check publisher IDs, and detect seller type mismatches before they cost you revenue.',
    features: ['Fetch & validate sellers.json from any SSP', 'Look up individual seller IDs instantly', 'Cross-check ads.txt records vs sellers.json', 'Detect DIRECT/RESELLER mismatches'],
  },
  '/crawler': {
    icon: Globe,
    name: 'Ads.txt Crawler',
    color: 'amber',
    pitch: 'Crawl up to 500 publisher domains in parallel. Know exactly which SSPs appear in your supply chain — and which ones are missing.',
    features: ['Crawl 500 domains simultaneously', 'Search for specific ads.txt lines across all domains', 'Auto sellers.json cross-check per node', 'Duplicate detection with exact file line output'],
  },
  '/bundles': {
    icon: Package,
    name: 'Bundle Tracker',
    color: 'purple',
    pitch: 'Stop manually tracking app bundle IDs in spreadsheets. Manage approved and rejected lists, bulk import, and check bundle status in seconds.',
    features: ['Approved & rejected bundle lists', 'Bulk import hundreds of bundle IDs at once', 'Instant status checks across all lists', 'Platform detection (iOS / Android / Web)'],
  },
  '/schain': {
    icon: Link2,
    name: 'Schain Validator',
    color: 'red',
    pitch: 'Validate supply chain objects node-by-node. Catch incomplete chains, missing seller IDs, and ads.txt mismatches before they block your bids.',
    features: ['Full oRTB SupplyChain object validation', 'Node-by-node sellers.json lookup', 'Publisher ads.txt / app-ads.txt cross-check', 'Visual schain builder from scratch'],
  },
  '/bundle-lookup': {
    icon: Layers,
    name: 'Bundle Lookup',
    color: 'cyan',
    pitch: 'Stop manually hunting for app details. Paste any bundle ID — iOS or Android — and instantly get the app name, developer name, store URL, developer domain, and auto-generated app-ads.txt URL. Plus see which DSPs have it approved or rejected.',
    features: [
      'iOS lookup via iTunes API — instant results',
      'Android lookup via Google Play store page',
      'Auto-extracts developer domain from store listing',
      'Generates app-ads.txt URL automatically',
      'DSP approval/rejection status per bundle',
      'Bulk lookup up to 50 IDs at once + CSV export'
    ]
  },
  '/app-category': {
    icon: Tag,
    name: 'App Category Segmenter',
    color: 'purple',
    pitch: 'Paste any list of iOS or Android bundle IDs and instantly see what categories they belong to — Games, Finance, Health, Entertainment and more. Results are grouped into visual segments with a distribution chart.',
    features: [
      'iOS lookup via iTunes API — accurate genre data',
      'Android lookup via Google Play — category scraping',
      'Visual stacked bar chart showing category distribution',
      'Per-category breakdown with app name, developer, rating',
      'Handles mixed iOS + Android lists in one run',
      'Export full segmentation report to CSV'
    ]
  },
  '/disc-monitor': {
    icon: Activity,
    name: 'Discrepancy Monitor',
    color: 'cyan',
    pitch: 'Set up continuous monitoring of Impressions and Revenue discrepancies between your Ad Server and all supply/demand partners. File import for manual checks, or API URLs for automatic twice-daily monitoring.',
    features: [
      'Compare Ad Server vs SSPs and DSPs simultaneously',
      'File mode: upload CSV/XLSX reports and run checks on demand',
      'API mode: auto-scans at 8AM + 8PM daily',
      'Tracks Impressions and Revenue with severity flagging',
      'Dashboard notifications for critical discrepancies',
      'Full run history with CSV export'
    ]
  },
  '/discrepancy': {
    icon: GitCompare,
    name: 'Discrepancy Checker',
    color: 'amber',
    pitch: 'Stop manually reconciling reports in spreadsheets. Upload CSVs or connect API URLs from both parties and instantly see where Wins, Impressions, eCPM and Revenue diverge — by date or broken down by Bundle ID.',
    features: [
      'Numbers check: totals by date across all metrics',
      'Bundle-wise check: per-app discrepancy breakdown',
      'Upload CSV, XLSX or connect API reporting URLs',
      'Color-coded severity: OK / Warning / Critical',
      'Flags missing dates or bundles in either report',
      'Export full discrepancy report to CSV'
    ]
  },
  '/bid-request': {
    icon: Radio,
    name: 'Bid req Analyzer',
    color: 'cyan',
    pitch: 'Paste any OpenRTB 2.5/2.6 bid request and get a full compliance audit — privacy signals, schain integrity, impression validation, and more.',
    features: ['Full oRTB 2.5 & 2.6 spec validation', 'GDPR, GPP, CCPA, COPPA, LMT signal detection', 'Schain extraction & node validation', 'Auto publisher domain lookup from storeurl'],
  },
};

const ALL_TOOLS = [
  { icon: Search, label: 'Sellers.json Checker', color: 'green' },
  { icon: Globe, label: 'Ads.txt Crawler', color: 'amber' },
  { icon: Package, label: 'Bundle Tracker', color: 'purple' },
  { icon: Link2, label: 'Schain Validator', color: 'red' },
  { icon: Radio, label: 'Bid req Analyzer', color: 'cyan' },
  { icon: Layers, label: 'Bundle Lookup', color: 'cyan' },
  { icon: GitCompare, label: 'Discrepancy Checker', color: 'amber' },
  { icon: Tag, label: 'App Category Segmenter', color: 'purple' },
  { icon: Activity, label: 'Discrepancy Monitor', color: 'cyan' },
];

export default function SubscriptionGate({ toolPath }) {
  const [copied, setCopied] = useState(false);
  const tool = TOOL_DETAILS[toolPath] || {};
  const Icon = tool.icon || Shield;
  const color = tool.color || 'cyan';

  function copyEmail() {
    navigator.clipboard.writeText('Support@ignitorads.com');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div style={{
      minHeight: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-start',
      paddingBottom: '40px'
    }}>

      {/* Hero lock block */}
      <div style={{
        width: '100%',
        maxWidth: '680px',
        margin: '0 auto',
      }}>

        {/* Tool identity */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: '14px',
          marginBottom: '24px'
        }}>
          <div style={{
            width: 48, height: 48,
            background: `var(--${color}-dim)`,
            border: `1px solid var(--${color})`,
            borderRadius: 'var(--radius)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: `var(--${color})`, flexShrink: 0,
            boxShadow: `0 0 20px var(--${color}-dim)`
          }}>
            <Icon size={22} />
          </div>
          <div>
            <h1 className="page-title" style={{ marginBottom: '2px' }}>{tool.name || 'Premium Tool'}</h1>
            <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: 0 }}>
              Part of the IgnitorAds Ad Compliance Suite
            </p>
          </div>
          <div style={{ marginLeft: 'auto' }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              background: 'color-mix(in srgb, var(--amber) 10%, transparent)',
              border: '1px solid color-mix(in srgb, var(--amber) 30%, transparent)',
              borderRadius: '20px', padding: '5px 12px',
              fontSize: '11px', color: 'var(--amber)', fontWeight: 600
            }}>
              <Lock size={11} />
              Pro Feature
            </div>
          </div>
        </div>

        {/* Pitch */}
        <div style={{
          background: 'var(--bg-card)',
          border: `1px solid var(--border)`,
          borderTop: `3px solid var(--${color})`,
          borderRadius: 'var(--radius)',
          padding: '24px',
          marginBottom: '16px'
        }}>
          <p style={{
            fontSize: '15px', color: 'var(--text-primary)',
            lineHeight: 1.7, marginBottom: '20px',
            fontFamily: 'var(--font-display)'
          }}>
            {tool.pitch}
          </p>

          {/* Feature list */}
          {tool.features && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '24px' }}>
              {tool.features.map((f, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'flex-start', gap: '8px',
                  fontSize: '12px', color: 'var(--text-secondary)'
                }}>
                  <CheckCircle size={13} style={{ color: `var(--${color})`, marginTop: '1px', flexShrink: 0 }} />
                  {f}
                </div>
              ))}
            </div>
          )}

          {/* CTA */}
          <div style={{
            background: 'var(--bg-elevated)',
            border: '1px solid var(--border-bright)',
            borderRadius: 'var(--radius)',
            padding: '20px',
            textAlign: 'center'
          }}>
            <div style={{
              fontSize: '13px', color: 'var(--text-muted)',
              marginBottom: '12px', lineHeight: 1.6
            }}>
              Get access to this tool and the full IgnitorAds compliance suite.<br />
              <span style={{ color: 'var(--text-secondary)' }}>
                Used by ad ops teams to protect supply chain integrity and maximize bid eligibility.
              </span>
            </div>

            <a
              href="mailto:Support@ignitorads.com?subject=Subscription%20Inquiry%20—%20AdTech%20Dashboard&body=Hi%2C%0A%0AI%20would%20like%20to%20subscribe%20to%20the%20IgnitorAds%20Ad%20Compliance%20Suite%20to%20access%20the%20premium%20tools.%0A%0APlease%20send%20me%20more%20information%20about%20pricing%20and%20plans.%0A%0AThanks"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '8px',
                background: 'var(--accent)', color: 'var(--on-accent)',
                padding: '11px 24px', borderRadius: 'var(--radius-sm)',
                fontFamily: 'var(--font-body)', fontSize: '13px', fontWeight: 600,
                textDecoration: 'none', marginBottom: '12px',
                boxShadow: 'var(--accent-glow)',
                transition: 'all 0.15s'
              }}
              onMouseEnter={e => e.currentTarget.style.filter = 'brightness(1.12)'}
              onMouseLeave={e => e.currentTarget.style.filter = 'none'}
            >
              <Mail size={14} />
              Get Access — Contact Us
              <ArrowRight size={14} />
            </a>

            <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
              or email us directly at{' '}
              <button
                onClick={copyEmail}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--cyan)', fontFamily: 'var(--font-mono)',
                  fontSize: '11px', padding: '0', textDecoration: 'underline'
                }}
              >
                Support@ignitorads.com
              </button>
              {copied && <span style={{ color: 'var(--green)', marginLeft: '6px' }}>✓ copied!</span>}
            </div>
          </div>
        </div>

        {/* What's included in subscription */}
        <div style={{
          background: 'var(--bg-card)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          padding: '20px',
          marginBottom: '16px'
        }}>
          <div style={{
            fontSize: '11px', color: 'var(--text-muted)',
            textTransform: 'uppercase', letterSpacing: '1px',
            marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '6px'
          }}>
            <Zap size={11} style={{ color: 'var(--amber)' }} />
            Everything included in your subscription
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px' }}>
            {/* Free tool */}
            <div style={{
              padding: '10px 12px',
              background: 'var(--green-dim)',
              border: '1px solid color-mix(in srgb, var(--green) 20%, transparent)',
              borderRadius: 'var(--radius-sm)',
              fontSize: '11px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' }}>
                <Shield size={11} style={{ color: 'var(--green)' }} />
                <span style={{ color: 'var(--green)', fontWeight: 600 }}>Free</span>
              </div>
              <div style={{ color: 'var(--text-secondary)' }}>Ads.txt Checker</div>
            </div>
            {/* Pro tools */}
            {ALL_TOOLS.map(({ icon: TIcon, label, color: tc }) => (
              <div key={label} style={{
                padding: '10px 12px',
                background: 'var(--bg-elevated)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-sm)',
                fontSize: '11px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' }}>
                  <TIcon size={11} style={{ color: `var(--${tc})` }} />
                  <span style={{ color: `var(--${tc})`, fontWeight: 600 }}>Pro</span>
                </div>
                <div style={{ color: 'var(--text-secondary)' }}>{label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Trust line */}
        <div style={{ textAlign: 'center', fontSize: '11px', color: 'var(--text-muted)', lineHeight: 1.8 }}>
          <span style={{ color: 'var(--text-secondary)' }}>IgnitorAds Ad Compliance Suite</span>
          {' · '}
          Built for publishers, ad ops teams, and SSP integrations
          {' · '}
          <a href="mailto:Support@ignitorads.com" style={{ color: 'var(--cyan)', textDecoration: 'none' }}>
            Support@ignitorads.com
          </a>
        </div>

      </div>
    </div>
  );
}
